// A club's tags, drawn: the badge row and the tickets button.
//
// One component for the attendee page, Events Admin and the creator's own card, so
// a club ticking "free food" sees on its dashboard the same mark an attendee
// sees on the map. What the tags MEAN lives in `src/utils/eventTags.js`; this
// is only how they look.
//
// The campus map at /umanitoba deliberately does NOT use this. `src/pathfinder/`
// is a copy-out of UMapsTools and imports nothing from the rest of the app, so
// the host hands it the already-decided `tags` and `ticket` and it draws them in
// its own stylesheet. Two drawings, one vocabulary — which is the trade that
// keeps the folder portable.
import { eventTags, ticketOf } from '../utils/eventTags.js';

/**
 * The badge row. Renders nothing at all when there are no tags, so it can be
 * dropped into a layout unconditionally without leaving a gap.
 */
export function EventTags({ event, className = '' }) {
  const tags = eventTags(event);
  if (!tags.length) return null;
  return (
    <div className={`et-tags ${className}`.trim()}>
      {/* ⚠️ The key is the POSITION, not `t.key`: a club's custom tags all carry
          the key 'custom' on purpose (they are one kind of thing and one CSS
          modifier), so three of them would be three identical React keys. The
          row is rebuilt whole from the event every render and never reordered,
          so the index is stable for exactly as long as it needs to be. */}
      {tags.map((t, i) => (
        <span key={i} className={`et-tag et-tag-${t.key}`} title={t.title || undefined}>
          {t.key === 'food' && <FoodMark />}
          {t.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Buy tickets — full width, under everything else.
 *
 * Wide because it is the one action on an event that is not about getting
 * there, and it competes with nothing: by the time somebody is reading this
 * they have decided to come. An external link in a new tab, since it leads to
 * whatever seller the club uses and losing the event page on the way there is
 * how somebody ends up unable to find the room.
 */
export function TicketButton({ event }) {
  const t = ticketOf(event);
  if (!t) return null;
  return (
    <a className="et-tickets" href={t.url} target="_blank" rel="noopener noreferrer">
      <TicketMark />
      {t.label}
    </a>
  );
}

/** A plate, for free food. Drawn rather than an emoji — see ParkConnect's Icons. */
function FoodMark() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4v6a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3V4M7 4v5M17 4c-1.5 1-2 2.5-2 4.5S15.5 12 17 13v7M7 13v7" />
    </svg>
  );
}

function TicketMark() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
         strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9V6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5V9a3 3 0 0 0 0 6v2.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5V15a3 3 0 0 0 0-6z" />
      <path d="M14 5v2M14 11v2M14 17v2" />
    </svg>
  );
}

/** Styles, injected by whichever page mounts these. */
export const EVENT_TAGS_CSS = `
.et-tags { display:flex; flex-wrap:wrap; gap:6px; }
.et-tag { display:inline-flex; align-items:center; gap:5px; border-radius:999px;
  padding:3px 10px; font-size:12px; font-weight:700; line-height:1.5;
  border:1px solid transparent; white-space:nowrap; }
/* Green for the one that is an offer, indigo for the one that is a condition,
   grey for the ones the club wrote itself. Three tones and not six: a row of
   colours is decoration, and these have to be told apart at a glance on a card
   three hundred pixels wide.
   ⚠️ THE CUSTOM TAG IS THE QUIET ONE ON PURPOSE, and quieter than both of the
   others — no tint anybody has to decode, just the site's own grey. Free food
   is the loud one because it is the fact that decides whether somebody walks
   over, and the faculty line is loud because it is the one that decides whether
   they are welcome when they get there. A custom tag is a phrase we print and
   do not vouch for, and giving a club's own words the weight of a badge this
   site stands behind is the one way this feature could mislead anybody. Three
   grey pills also stay legible as a group, which the same three in green would
   not. */
.et-tag-food { background:#e7f6ec; color:#15803d; border-color:#bfe5cb; }
.et-tag-faculty { background:#eef2ff; color:#3730a3; border-color:#d7dcfb; }
.et-tag-custom { background:#f1f3f4; color:#3c4043; border-color:#dfe1e5; font-weight:600; }

.et-tickets { display:flex; align-items:center; justify-content:center; gap:8px;
  width:100%; box-sizing:border-box; margin-top:10px;
  background:#15181d; color:#fff; border-radius:12px; padding:12px 16px;
  font-size:14.5px; font-weight:800; text-decoration:none; }
.et-tickets:hover { background:#2a2f37; }
`;
